import { useState, useEffect, useCallback } from 'react';
import {
  fetchProjectEpisodeSummary,
  fetchProjectEpisodeDeliveries,
  fetchProjectEpisodes,
} from '@/service/api/project';
import { formatDate } from './utils';
import EpisodeVersionsModal from './EpisodeVersionsModal';
import ReplaceEpisodeModal from './ReplaceEpisodeModal';
import BatchAddEpisodesModal from './BatchAddEpisodesModal';
import Select, { SelectOption } from '@/components/common/Select';
import PortfolioPreviewModal from '@/components/common/PortfolioPreviewModal';
import Toast from '@/components/common/Toast';

interface EpisodesTabProps {
  project: Api.Project.ProjectDetailResponse;
}

const SOURCE_OPTIONS: SelectOption[] = [
  { label: '全部来源', value: '' },
  { label: '申请时上传', value: 'APPLICATION_INITIAL' },
  { label: '项目后续提交', value: 'PROJECT_FOLLOW_UP' },
];

export default function EpisodesTab({ project }: EpisodesTabProps) {
  const projectId = project.id || '';

  // 摘要数据
  const [summary, setSummary] = useState<Api.Project.EpisodeSummaryResponse | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);

  // 全量剧集及当前审核状态列表
  const [episodes, setEpisodes] = useState<Api.Project.ProjectEpisodeResponse[]>([]);
  const [isEpisodesLoading, setIsEpisodesLoading] = useState(false);

  // 交付清单列表数据
  const [deliveries, setDeliveries] = useState<Api.Project.EpisodeDeliveryResponse[]>([]);
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [isDeliveriesLoading, setIsDeliveriesLoading] = useState(false);

  // 当前视图：episodes (全量剧集审核状态) | deliveries (交付历史明细)
  const [activeView, setActiveView] = useState<'episodes' | 'deliveries'>('episodes');

  // 弹窗状态
  const [activeEpNoForVersions, setActiveEpNoForVersions] = useState<number | null>(null);
  const [replaceModalEpNo, setReplaceModalEpNo] = useState<number | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // 视频预览 Modal 状态
  const [previewMedia, setPreviewMedia] = useState<{ title: string; url: string; isVideo?: boolean } | null>(null);

  // 提示 Toast 消息
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  /** 加载剧集交付摘要 */
  const loadSummary = useCallback(async () => {
    if (!projectId) return;
    setIsSummaryLoading(true);
    try {
      const data = await fetchProjectEpisodeSummary(projectId);
      setSummary(data || null);
    } catch (err) {
      console.error('获取剧集交付摘要失败:', err);
    } finally {
      setIsSummaryLoading(false);
    }
  }, [projectId]);

  /** 加载全量剧集及当前审核状态 (GET /my/projects/{id}/episodes) */
  const loadEpisodes = useCallback(async () => {
    if (!projectId) return;
    setIsEpisodesLoading(true);
    try {
      const data = await fetchProjectEpisodes(projectId);
      setEpisodes(data || []);
    } catch (err) {
      console.error('获取项目剧集及审核状态失败:', err);
    } finally {
      setIsEpisodesLoading(false);
    }
  }, [projectId]);

  /** 加载交付清单 */
  const loadDeliveries = useCallback(async () => {
    if (!projectId) return;
    setIsDeliveriesLoading(true);
    try {
      const res = await fetchProjectEpisodeDeliveries(projectId, {
        source: sourceFilter || undefined,
        page,
        page_size: pageSize,
      });
      setDeliveries(res?.items || []);
      setTotal(res?.total || 0);
    } catch (err) {
      console.error('获取剧集交付清单失败:', err);
    } finally {
      setIsDeliveriesLoading(false);
    }
  }, [projectId, sourceFilter, page, pageSize]);

  useEffect(() => {
    loadSummary();
    loadEpisodes();
  }, [loadSummary, loadEpisodes]);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries]);

  /** 状态 Badge 渲染 */
  const renderStatusBadge = (status?: string) => {
    switch (status) {
      case 'PASSED':
      case 'APPROVED':
        return <span className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">审核通过</span>;
      case 'PENDING':
      case 'PENDING_REVIEW':
      case 'SUBMITTED':
        return <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">待审核</span>;
      case 'REJECTED':
        return <span className="text-xs px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">已驳回</span>;
      case 'REPLACED':
        return <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">已替换</span>;
      case 'UNSUBMITTED':
      case 'DRAFT':
        return <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">未提交</span>;
      default:
        return <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">{status || '处理中'}</span>;
    }
  };

  const totalPages = Math.ceil(total / pageSize) || 1;

  const handleModalSuccess = (msg: string) => {
    setActionMessage({ type: 'success', msg });
    loadEpisodes();
    loadDeliveries();
    loadSummary();
  };

  const handleModalError = (msg: string) => {
    setActionMessage({ type: 'error', msg });
  };

  // 动态计算后续增量起始集号（取已有各列表中集号的最大值 + 1）
  const calculatedInitialEpNo = Math.max(
    project.total_episode_count || 0,
    summary?.total_episode_count || 0,
    ...episodes.map((e) => e.episode_no || 0),
    ...deliveries.map((d) => d.episode_no || 0),
    0
  ) + 1;

  return (
    <div>
      {/* 头部统计与操作 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="font-semibold text-foreground-950 text-base flex items-center gap-2">
            <i className="ri-clapperboard-line text-primary-600"></i>
            剧集交付与审核管理
          </h3>
          <p className="text-xs text-foreground-500 mt-1">查看项目剧集审核状态、交付进度、文件版本记录与版本恢复操作</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { loadEpisodes(); loadSummary(); loadDeliveries(); }}
            className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-foreground-600 hover:bg-gray-50 flex items-center gap-1 transition-all"
          >
            <i className="ri-refresh-line"></i> 刷新数据
          </button>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="px-3.5 py-1.5 rounded-lg bg-primary-500 text-foreground-950 font-medium text-xs hover:bg-primary-400 flex items-center gap-1 transition-all shadow-sm"
          >
            <i className="ri-add-line font-bold"></i> 新增后续剧集
          </button>
        </div>
      </div>

      {/* 剧集交付摘要卡片 */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <div className="p-4 rounded-xl bg-background-50 border border-background-200/60 text-center">
          <p className="text-xl font-bold text-foreground-900">{isSummaryLoading ? '-' : (summary?.total_episode_count ?? project.total_episode_count ?? episodes.length ?? 0)}</p>
          <p className="text-xs text-foreground-500 mt-1">预计总集数</p>
        </div>
        <div className="p-4 rounded-xl bg-blue-50/50 border border-blue-100 text-center">
          <p className="text-xl font-bold text-blue-700">{isSummaryLoading ? '-' : (summary?.delivered_count ?? deliveries.length)}</p>
          <p className="text-xs text-blue-600 mt-1">已交付集数</p>
        </div>
        <div className="p-4 rounded-xl bg-emerald-50/50 border border-emerald-100 text-center">
          <p className="text-xl font-bold text-emerald-700">{isSummaryLoading ? '-' : (summary?.passed_count ?? 0)}</p>
          <p className="text-xs text-emerald-600 mt-1">审核通过</p>
        </div>
        <div className="p-4 rounded-xl bg-amber-50/50 border border-amber-100 text-center">
          <p className="text-xl font-bold text-amber-700">{isSummaryLoading ? '-' : (summary?.pending_review_count ?? 0)}</p>
          <p className="text-xs text-amber-600 mt-1">待审核中</p>
        </div>
        <div className="p-4 rounded-xl bg-rose-50/50 border border-rose-100 text-center">
          <p className="text-xl font-bold text-rose-700">{isSummaryLoading ? '-' : (summary?.rejected_count ?? 0)}</p>
          <p className="text-xs text-rose-600 mt-1">已驳回集数</p>
        </div>
      </div>

      {/* 缺失集数警示提示 */}
      {summary?.missing_episode_nos && summary.missing_episode_nos.length > 0 && (
        <div className="mb-6 p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
          <i className="ri-alert-line text-sm mt-0.5 text-amber-600"></i>
          <div>
            <span className="font-semibold">部分剧集未按顺序交付：</span>
            <span>当前缺失第 {summary.missing_episode_nos.join('、')} 集视频，请及时补充上传。</span>
          </div>
        </div>
      )}

      {/* 视图切换与筛选工具条 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-background-100 text-xs">
        <div className="flex items-center gap-2">
          <div className="inline-flex p-1 rounded-lg bg-gray-100/80 border border-gray-200/60">
            <button
              onClick={() => setActiveView('episodes')}
              className={`px-3 py-1 rounded-md transition-all text-xs font-medium cursor-pointer ${
                activeView === 'episodes'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              全量剧集审核状态 ({episodes.length})
            </button>
            <button
              onClick={() => setActiveView('deliveries')}
              className={`px-3 py-1 rounded-md transition-all text-xs font-medium cursor-pointer ${
                activeView === 'deliveries'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              交付历史记录 ({total})
            </button>
          </div>

          {activeView === 'deliveries' && (
            <div className="flex items-center gap-2 ml-2">
              <span className="text-foreground-500 shrink-0">交付来源:</span>
              <Select
                size="sm"
                className="w-48"
                options={SOURCE_OPTIONS}
                value={sourceFilter}
                onChange={(val) => {
                  setSourceFilter(String(val));
                  setPage(1);
                }}
              />
            </div>
          )}
        </div>

        <span className="text-foreground-400">
          {activeView === 'episodes' ? `共 ${episodes.length} 条剧集记录` : `共 ${total} 条交付记录`}
        </span>
      </div>

      {/* 视图内容展示 */}
      {activeView === 'episodes' ? (
        /* 全量剧集及当前审核状态表格 */
        isEpisodesLoading ? (
          <div className="py-16 text-center text-foreground-400 text-sm">
            <i className="ri-loader-4-line animate-spin text-xl block mb-2 text-primary-500"></i>
            加载剧集审核状态...
          </div>
        ) : episodes.length === 0 ? (
          <div className="py-16 text-center text-foreground-400 text-xs bg-background-50/50 rounded-xl border border-background-200/50">
            <i className="ri-inbox-line text-3xl block mb-2 text-foreground-300"></i>
            暂无项目剧集记录
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-background-200/70 text-left text-foreground-500 font-medium">
                  <th className="py-3 px-3">集号</th>
                  <th className="py-3 px-3">单集标题</th>
                  <th className="py-3 px-3">当前视频文件</th>
                  <th className="py-3 px-3">审核状态</th>
                  <th className="py-3 px-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {episodes.map((item) => {
                  const epNo = item.episode_no ?? 0;
                  const file = item.current_file;

                  return (
                    <tr key={item.id || epNo} className="border-b border-background-200/50 hover:bg-gray-50/60 transition-colors">
                      <td className="py-3 px-3 font-semibold text-foreground-900">
                        第 {epNo} 集
                      </td>
                      <td className="py-3 px-3 text-foreground-800">
                        {item.episode_title || `第${epNo}集`}
                      </td>
                      <td className="py-3 px-3">
                        {file ? (
                          <div className="flex flex-col">
                            <span className="font-medium text-foreground-900 max-w-[200px] truncate" title={file.file_name || ''}>
                              {file.file_name || '已关联文件'}
                            </span>
                            {file.access_url && (
                              <div className="flex items-center gap-2 mt-0.5">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPreviewMedia({
                                      title: `第 ${epNo} 集 ${item.episode_title ? `- ${item.episode_title}` : ''}`,
                                      url: file.access_url!,
                                      isVideo: true,
                                    })
                                  }
                                  className="text-[11px] text-primary-600 hover:underline flex items-center gap-0.5 cursor-pointer font-medium"
                                >
                                  <i className="ri-play-circle-line text-xs"></i> 在线预览
                                </button>
                                <a
                                  href={file.access_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[11px] text-foreground-400 hover:text-foreground-600 hover:underline flex items-center gap-0.5"
                                >
                                  <i className="ri-download-line text-xs"></i> 下载
                                </a>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-foreground-400 italic">未上传文件</span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <div>
                          {renderStatusBadge(item.status)}
                          {item.rejection_reason && (
                            <p className="text-[11px] text-rose-600 mt-1 max-w-[200px]" title={item.rejection_reason}>
                              原因: {item.rejection_reason}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-right space-x-2">
                        <button
                          onClick={() => setActiveEpNoForVersions(epNo)}
                          className="px-2.5 py-1 rounded border border-gray-200 hover:border-primary-300 hover:text-primary-600 text-foreground-600 transition-colors cursor-pointer"
                          title="查看文件版本历史并可在线恢复"
                        >
                          <i className="ri-history-line mr-0.5"></i> 历史版本
                        </button>
                        <button
                          onClick={() => setReplaceModalEpNo(epNo)}
                          className="px-2.5 py-1 rounded bg-gray-100 hover:bg-gray-200 text-foreground-800 transition-colors cursor-pointer"
                        >
                          <i className="ri-replace-line mr-0.5"></i> 替换文件
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : (
        /* 交付列表表格 */
        isDeliveriesLoading ? (
          <div className="py-16 text-center text-foreground-400 text-sm">
            <i className="ri-loader-4-line animate-spin text-xl block mb-2 text-primary-500"></i>
            加载剧集清单...
          </div>
        ) : deliveries.length === 0 ? (
          <div className="py-16 text-center text-foreground-400 text-xs bg-background-50/50 rounded-xl border border-background-200/50">
            <i className="ri-inbox-line text-3xl block mb-2 text-foreground-300"></i>
            暂无剧集交付记录
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-background-200/70 text-left text-foreground-500 font-medium">
                    <th className="py-3 px-3">集号</th>
                    <th className="py-3 px-3">单集标题</th>
                    <th className="py-3 px-3">当前视频文件</th>
                    <th className="py-3 px-3">交付来源</th>
                    <th className="py-3 px-3">提交时间</th>
                    <th className="py-3 px-3">状态</th>
                    <th className="py-3 px-3 text-right">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.map((item) => {
                    const epNo = item.episode_no ?? 0;
                    const file = item.current_file;
                    const sourceLabel = item.source === 'APPLICATION_INITIAL' ? '申请初始提交' : (item.source === 'PROJECT_FOLLOW_UP' ? '后续项目交付' : item.source || '-');

                    return (
                      <tr key={item.id || epNo} className="border-b border-background-200/50 hover:bg-gray-50/60 transition-colors">
                        <td className="py-3 px-3 font-semibold text-foreground-900">
                          第 {epNo} 集
                        </td>
                        <td className="py-3 px-3 text-foreground-800">
                          {item.episode_title || `第${epNo}集`}
                        </td>
                        <td className="py-3 px-3">
                          {file ? (
                            <div className="flex flex-col">
                              <span className="font-medium text-foreground-900 max-w-[200px] truncate" title={file.file_name || ''}>
                                {file.file_name || '已关联文件'}
                              </span>
                              {file.access_url && (
                                <div className="flex items-center gap-2 mt-0.5">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setPreviewMedia({
                                        title: `第 ${epNo} 集 ${item.episode_title ? `- ${item.episode_title}` : ''}`,
                                        url: file.access_url!,
                                        isVideo: true,
                                      })
                                    }
                                    className="text-[11px] text-primary-600 hover:underline flex items-center gap-0.5 cursor-pointer font-medium"
                                  >
                                    <i className="ri-play-circle-line text-xs"></i> 在线预览
                                  </button>
                                  <a
                                    href={file.access_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[11px] text-foreground-400 hover:text-foreground-600 hover:underline flex items-center gap-0.5"
                                  >
                                    <i className="ri-download-line text-xs"></i> 下载
                                  </a>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-foreground-400 italic">未上传文件</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-foreground-600">
                          <span className="px-2 py-0.5 rounded bg-gray-100 text-foreground-700">
                            {sourceLabel}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-foreground-500">
                          {formatDate(item.submitted_at)}
                        </td>
                        <td className="py-3 px-3">
                          <div>
                            {renderStatusBadge(item.status)}
                            {item.rejection_reason && (
                              <p className="text-[11px] text-rose-600 mt-1 max-w-[180px]" title={item.rejection_reason}>
                                原因: {item.rejection_reason}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-right space-x-2">
                          <button
                            onClick={() => setActiveEpNoForVersions(epNo)}
                            className="px-2.5 py-1 rounded border border-gray-200 hover:border-primary-300 hover:text-primary-600 text-foreground-600 transition-colors cursor-pointer"
                            title="查看文件版本历史并可在线恢复"
                          >
                            <i className="ri-history-line mr-0.5"></i> 历史版本
                          </button>
                          <button
                            onClick={() => setReplaceModalEpNo(epNo)}
                            className="px-2.5 py-1 rounded bg-gray-100 hover:bg-gray-200 text-foreground-800 transition-colors cursor-pointer"
                          >
                            <i className="ri-replace-line mr-0.5"></i> 替换文件
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* 分页控制 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-background-100 text-xs text-foreground-500">
                <span>第 {page} / {totalPages} 页</span>
                <div className="flex items-center gap-2">
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    上一页
                  </button>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                    className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </>
        )
      )}

      {/* 历史版本 Modal */}
      <EpisodeVersionsModal
        open={activeEpNoForVersions !== null}
        projectId={projectId}
        epNo={activeEpNoForVersions}
        onClose={() => setActiveEpNoForVersions(null)}
        onSuccess={handleModalSuccess}
        onError={handleModalError}
      />

      {/* 替换剧集 Modal */}
      <ReplaceEpisodeModal
        open={replaceModalEpNo !== null}
        projectId={projectId}
        epNo={replaceModalEpNo}
        onClose={() => setReplaceModalEpNo(null)}
        onSuccess={handleModalSuccess}
        onError={handleModalError}
      />

      {/* 批量新增剧集 Modal */}
      <BatchAddEpisodesModal
        open={isAddModalOpen}
        projectId={projectId}
        initialEpisodeNo={calculatedInitialEpNo}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleModalSuccess}
        onError={handleModalError}
      />

      {/* 视频作品在线预览 Modal */}
      <PortfolioPreviewModal
        open={previewMedia !== null}
        title={previewMedia?.title || '剧集作品预览'}
        isVideo={previewMedia?.isVideo ?? true}
        mediaUrl={previewMedia?.url}
        onClose={() => setPreviewMedia(null)}
      />

      {/* 提示消息 Toast */}
      <Toast
        message={actionMessage?.msg || ''}
        type={actionMessage?.type || 'info'}
        onClose={() => setActionMessage(null)}
      />
    </div>
  );
}
